import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
// import { useNavigate } from "react-router-dom";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import Icon from "Components/Icons/Icon";
import CustomButton from "Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { QuoteDetailsPayload } from "types/quote.api";
import {
  addNewClient,
  createQuote,
  deleteAttachment,
  editQuote,
  getClients,
} from "slices/thunks";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import {
  addDaysToToday,
  formatDateToYMD,
  formatToDayMonYear,
} from "utils/formatDates";
import { PresetQuotePayload } from "types/preset.api";
import { AddClientFormParams, ClientsPayloadData } from "types/client.api";
import ManageClientModal from "Components/Client/ManageClientModal";
import { resetClientStatus } from "slices/clients/reducer";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  createDescriptionValidation,
  createNameValidation,
  createOptionalDescriptionValidation,
} from "Components/constants/validation";
import { ErrorHandler, ErrorToast } from "utils/toast";
import { compressImage } from "utils/compressImage";

interface OptionType {
  label: string;
  value: string;
}

interface SummaryFormValues {
  title: string;
  reference_number: string;
  quote_date: string;
  expiry_date: string;
  client: OptionType | null;
  job_description: string;
  notes: string;
  attachments: { name: string; uri: string; type: string }[] | null;
}

interface ManageQuoteSummaryPaneProps {
  template: string;
  categorised: string;
  isEdit: boolean;
  quoteData: QuoteDetailsPayload | null;
  onSubmit: () => void;
  presetQuote: PresetQuotePayload | null;
  onDatesChange: (
    quoteDate?: string | undefined,
    expiryDate?: string | undefined,
  ) => void;
  onClientOptionChange: (option: ClientsPayloadData | undefined) => void;
  onJobDescriptionChange?: (description: string) => void;
  clientPayload?: ClientsPayloadData;
  onQuoteTitleChange?: (title: string) => void;
  isHidePhoneNumber?: boolean;
  handleHidePhoneNumberToggle?: () => void;
}

const SummaryPane: React.FC<ManageQuoteSummaryPaneProps> = ({
  template,
  categorised,
  isEdit,
  quoteData,
  onSubmit,
  presetQuote = null,
  onDatesChange,
  onClientOptionChange,
  onJobDescriptionChange,
  onQuoteTitleChange,
  clientPayload,
  isHidePhoneNumber = false,
  handleHidePhoneNumberToggle,
}) => {
  const dispatch = useAppDispatch();
  const configData = useAppSelector((state) => state.config.configData);
  const {
    clients,
    isLoading,
    status: addStatus,
  } = useAppSelector((state) => state.client);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  // const { status, quoteDetails } = useAppSelector((state) => state.quote);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const editQuoteLoading = useAppSelector((state) => state.quote.status.edit);
  const addQuoteLoading = useAppSelector((state) => state.quote.status.add);

  // Debounce search term (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // API call only when debounced search term changes
  useEffect(() => {
    dispatch(getClients({ search: debouncedSearchTerm }));
  }, [debouncedSearchTerm, dispatch]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const clientOptions = clients.map((client) => {
    return {
      label: client.name,
      value: client.id,
    };
  });

  const validation: FormikProps<SummaryFormValues> =
    useFormik<SummaryFormValues>({
      enableReinitialize: true,

      initialValues: {
        title: quoteData?.title || "",
        reference_number: quoteData?.reference_number || "QT-2025-01",
        quote_date:
          quoteData?.quote_date || new Date().toISOString().split("T")[0],
        expiry_date:
          quoteData?.expiry_date ||
          addDaysToToday(configData?.billing_preferences.quote_expiration || 0),
        client: quoteData
          ? { label: quoteData.client.name, value: String(quoteData.client.id) }
          : null,
        job_description:
          quoteData?.job_description || presetQuote?.description || "",
        notes: quoteData?.notes || "",
        attachments: [],
      },

      validationSchema: Yup.object({
        title: Yup.string()
          .required(VALIDATION_MESSAGES.REQUIRED("quote title"))
          .trim()
          .min(
            VALIDATION_LIMITS.TITLE.MIN,
            VALIDATION_MESSAGES.MIN_LENGTH(
              "Title",
              VALIDATION_LIMITS.TITLE.MIN,
            ),
          )
          .max(
            VALIDATION_LIMITS.TITLE.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH(
              "Title",
              VALIDATION_LIMITS.TITLE.MAX,
            ),
          )
          .test(
            "valid-title",
            createNameValidation("Title").message,
            createNameValidation("Title").test,
          ),
        reference_number: Yup.string().required("Reference number is required"),
        quote_date: Yup.string().required("Quote date is required"),
        expiry_date: Yup.string()
          .required("Expiry date is required")
          .test(
            "is-after-quote-date",
            "Expiry date must be the same or after quote date",
            function (value) {
              const { quote_date } = this.parent;

              if (!quote_date || !value) return true; // Already handled by required()

              return new Date(value) >= new Date(quote_date);
            },
          ),

        client: Yup.object().nullable().required("Select a client"),
        job_description: Yup.string()
          .trim()
          .required(VALIDATION_MESSAGES.REQUIRED("job description"))
          .max(
            VALIDATION_LIMITS.DESCRIPTION.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH(
              "Job description",
              VALIDATION_LIMITS.DESCRIPTION.MAX,
            ),
          )
          .test(
            "valid-job-description",
            createDescriptionValidation("Job description").message,
            createDescriptionValidation("Job description").test,
          ),
        notes: Yup.string()
          .nullable()
          .trim()
          .max(
            VALIDATION_LIMITS.NOTES.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH(
              "Notes",
              VALIDATION_LIMITS.NOTES.MAX,
            ),
          )
          .test(
            "valid-notes",
            createOptionalDescriptionValidation("Notes").message,
            (value) =>
              createOptionalDescriptionValidation("Notes").test(
                value ?? undefined,
              ),
          ),
        attachments: Yup.array().max(
          5,
          "You can upload a maximum of 5 attachments",
        ),
      }),

      onSubmit: async (values) => {
        const data = {
          title: values.title?.trim(),
          description: values.job_description?.trim(),
          notes: values.notes?.trim() || "",
          quote_date: values.quote_date,
          expiry_date: values.expiry_date,
          client_id: values.client?.value ? parseInt(values.client.value) : 0,
          attachments: values.attachments || [],
          reference_number: values.reference_number?.trim(),
          categorised,
          template,
          is_company_phone_number_show: isHidePhoneNumber ? 0 : 1,
        };
        try {
          if (isEdit && quoteData) {
            // Handle edit logic
            const updatedQuote = {
              ...data,
              quote_id: quoteData.id,
              deposit_payment_method: quoteData.deposit_payment_method,
            };
            await dispatch(editQuote(updatedQuote))?.unwrap();
          } else {
            await dispatch(createQuote(data))?.unwrap();
          }
          onSubmit();
          setAttachments([]);
          setPreviewUrls([]);
        } catch (error) {
          ErrorHandler(error);
        }
      },
    });

  // Handle file selection with preview
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];

    // Validate file type - only allow PDF, JPEG, PNG, and GIF
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/gif",
      "application/pdf",
    ];
    const allowedExtensions = ["jpg", "jpeg", "png", "gif", "pdf"];

    // Check each file for valid type
    for (const file of files) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension || "")
      ) {
        e.target.value = ""; // Reset the input
        ErrorToast("Only PDF, JPEG, PNG, and GIF files are allowed");
        return;
      }
    }

    const existingCount = quoteData?.attachments?.length || 0;
    const newCount = attachments.length;
    const requestedCount = files.length;

    const totalCount = existingCount + newCount + requestedCount;

    if (totalCount > 5) {
      validation.setFieldTouched("attachments", true, false);
      validation.setFieldError(
        "attachments",
        "You can upload a maximum of 5 attachments",
      );

      e.target.value = ""; // reset input
      return;
    }

    try {
      // 🔥 Compress only images
      const processedFiles = await Promise.all(
        files.map(async (file) => {
          if (file.type.startsWith("image/")) {
            return await compressImage(file, 2); // 2MB limit
          }
          return file; // PDF untouched
        }),
      );

      const newPreviewUrls = processedFiles.map((file) =>
        URL.createObjectURL(file),
      );

      const updatedFiles = [...attachments, ...processedFiles];
      const updatedPreviewUrls = [...previewUrls, ...newPreviewUrls];

      setAttachments(updatedFiles);
      setPreviewUrls(updatedPreviewUrls);

      validation.setFieldValue("attachments", updatedFiles);
    } catch (error: any) {
      ErrorToast(error || "Image compression failed");
    }

    e.target.value = ""; // reset input safely
  };

  useEffect(() => {
    if (clients && validation?.values?.client) {
      const client = clients?.find(
        (c) => c?.id === Number(validation?.values?.client?.value),
      );
      onClientOptionChange(client);
    }
  }, [clients, validation?.values?.client]);

  useEffect(() => {
    if (addStatus.add === "succeeded" && clients.length > 0) {
      let client = clients[0];
      validation.setFieldValue("client", {
        label: client.name,
        value: String(client.id),
      });
      onClientOptionChange(client);
      dispatch(resetClientStatus());
    }
  }, [addStatus?.add]);

  // Delete specific attachment
  const handleDeleteAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    const newPreviewUrls = previewUrls.filter((_, i) => i !== index);

    // Revoke the deleted preview URL
    URL.revokeObjectURL(previewUrls[index]);

    setAttachments(newAttachments);
    setPreviewUrls(newPreviewUrls);
    validation.setFieldValue("attachments", newAttachments);
  };

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  useEffect(() => {
    if (clientPayload) {
      validation.setFieldValue("client", {
        label: clientPayload.name,
        value: clientPayload.id,
      });
    }
  }, []);

  return (
    <Row className="mt-4">
      <Col>
        <form onSubmit={validation.handleSubmit}>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <Label>Quote Title</Label>
                <Input
                  type="text"
                  name="title"
                  placeholder="e.g. Kitchen Renovation"
                  value={validation.values.title}
                  onBlur={validation.handleBlur}
                  onChange={(event) => {
                    const value = event.target.value;
                    validation.handleChange(event);
                    onQuoteTitleChange?.(value);
                  }}
                  invalid={
                    !!(validation.errors.title && validation.touched.title)
                  }
                />
                <FormFeedback>{validation.errors.title}</FormFeedback>
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label>Reference Number</Label>
                <Input
                  type="text"
                  name="reference_number"
                  placeholder="Enter reference number"
                  value={validation.values.reference_number}
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  invalid={
                    !!(
                      validation.errors.reference_number &&
                      validation.touched.reference_number
                    )
                  }
                  disabled
                />
                <FormFeedback>
                  {validation.errors.reference_number}
                </FormFeedback>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <Label>Quote Date</Label>
                <div className="position-relative">
                  <Flatpickr
                    className="form-control"
                    placeholder="YYYY-MM-DD"
                    value={validation.values.quote_date}
                    onChange={(date: any) => {
                      if (date[0]) {
                        const formattedDate = formatDateToYMD(date[0]);
                        validation.setFieldValue("quote_date", formattedDate);
                        const formattedDateChange = formatToDayMonYear(date[0]);
                        onDatesChange(formattedDateChange, undefined);
                      }
                    }}
                    options={{
                      maxDate: validation.values.expiry_date
                        ? new Date(validation.values.expiry_date)
                        : undefined,
                      dateFormat: "Y-m-d",
                      disableMobile: true,
                    }}
                  />
                  <Icon
                    name="Calendar"
                    className="position-absolute"
                    style={{
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                    }}
                  />
                </div>
                {validation.errors.quote_date &&
                  validation.touched.quote_date && (
                    <div className="text-danger fs-12 mt-1">
                      {validation.errors.quote_date}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label>Expiry Date</Label>
                <div className="position-relative">
                  <Flatpickr
                    className="form-control"
                    placeholder="YYYY-MM-DD"
                    value={
                      validation.values.expiry_date
                        ? new Date(validation.values.expiry_date)
                        : undefined
                    }
                    onChange={(date: any) => {
                      if (date[0]) {
                        const formattedDate = formatDateToYMD(date[0]);
                        validation.setFieldValue(
                          "expiry_date",
                          formattedDate,
                          true,
                        );
                        const formattedDateChange = formatToDayMonYear(date[0]);
                        onDatesChange(undefined, formattedDateChange);
                      }
                    }}
                    options={{
                      minDate: validation.values.quote_date
                        ? new Date(validation.values.quote_date)
                        : undefined,
                      dateFormat: "Y-m-d",
                      disableMobile: true,
                    }}
                  />
                  <Icon
                    name="Calendar"
                    className="position-absolute"
                    style={{
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                    }}
                  />
                </div>
                {validation.errors.expiry_date &&
                  validation.touched.expiry_date && (
                    <div className="text-danger fs-12 mt-1">
                      {validation.errors.expiry_date}
                    </div>
                  )}
              </div>
            </Col>
          </Row>

          <Row>
            <div className="d-flex gap-2 align-content-center mt-1">
              <p className="font-primary fs-14 text-black2D fw-normal">
                Hide your phone number
              </p>
              <div
                className={`toggle-switch ${isHidePhoneNumber ? "on" : "off"}`}
                onClick={handleHidePhoneNumberToggle}
              >
                <span
                  className={`toggle-label ${isHidePhoneNumber ? "on" : "off"}`}
                >
                  {isHidePhoneNumber ? "Yes" : "No"}
                </span>
                <div
                  className={`toggle-circle ${
                    isHidePhoneNumber ? "on" : "off"
                  }`}
                />
              </div>
            </div>
          </Row>

          <div className="divider my-4" />

          <div className="mb-3">
            <Label>Client</Label>
            <Col md={12}>
              <div className="d-flex gap-2 align-items-center justify-content-between">
                <CustomButton
                  newClassName="bg-white border-1 border-black align-items-center justify-content-center gap-1 "
                  text="New Client"
                  textColor="text-black2D"
                  leftIcon={
                    <i
                      className="ri-add-line align-bottom text-black2D"
                      style={{ fontSize: 17 }}
                    />
                  }
                  onClick={() => {
                    setShowClientModal(true);
                  }}
                />

                <div className="flex-fill field-inline">
                  <Select
                    value={validation.values.client}
                    onChange={(option: any) => {
                      console.log("option", option);
                      validation.setFieldValue("client", option);
                    }}
                    onInputChange={(inputValue: any) => {
                      handleSearch(inputValue);
                    }}
                    options={clientOptions}
                    placeholder="Search or select a client"
                    classNamePrefix="react-select"
                    className={`auth-form-dropdown-control`}
                    isLoading={isLoading}
                    loadingMessage={() => "Searching clients..."}
                    noOptionsMessage={({ inputValue }: { inputValue: any }) =>
                      inputValue
                        ? `No clients found for "${inputValue}"`
                        : "No clients available"
                    }
                    isClearable
                    isDisabled={isEdit}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                      control: (base: any) => ({
                        ...base,
                        borderColor: base.borderColor,
                        backgroundColor: isEdit ? "#F5F6FB" : "#fff",
                        "&:hover": {
                          borderColor: base.borderColor,
                        },
                      }),
                    }}
                  />
                </div>
              </div>
            </Col>

            {validation.errors.client && validation.touched.client && (
              <div className="text-danger fs-12">
                {validation.errors.client as string}
              </div>
            )}
          </div>

          <div className="mb-3 white">
            <Label>Job Description</Label>
            <Input
              type="textarea"
              name="job_description"
              rows={3}
              placeholder="Enter job details..."
              value={validation.values.job_description}
              onChange={(event) => {
                const value = event.target.value;
                validation.handleChange(event);
                onJobDescriptionChange?.(value);
              }}
              onBlur={validation.handleBlur}
              invalid={
                !!(
                  validation.errors.job_description &&
                  validation.touched.job_description
                )
              }
            />
            <FormFeedback>{validation.errors.job_description}</FormFeedback>
          </div>

          <div className="mb-3">
            <Label>Notes (Not visible on quote)</Label>
            <Input
              type="textarea"
              name="notes"
              rows={3}
              placeholder="Add private notes"
              value={validation.values.notes}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={!!(validation.errors.notes && validation.touched.notes)}
            />
            <FormFeedback>{validation.errors.notes}</FormFeedback>
          </div>

          <hr className="my-4" />

          <div className="mb-4">
            <Label>Attachments</Label>
            <Input
              type="file"
              name="attachments"
              accept="image/jpeg,image/png,image/gif,application/pdf"
              multiple
              onChange={handleFileChange}
              invalid={
                !!(
                  validation.errors.attachments &&
                  validation.touched.attachments
                )
              }
              value={""}
            />

            <FormFeedback>{validation.errors.attachments}</FormFeedback>

            {/* Preview with Delete Feature */}
            {attachments && attachments.length > 0 && (
              <div className="mt-3">
                <small className="text-muted fw-semibold">
                  {attachments.length} file(s) selected
                </small>
                <Row className="mt-2 g-3">
                  {attachments.map((file: File, index: number) => (
                    <Col xs={6} sm={4} md={3} key={index}>
                      <div className="card border position-relative">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute"
                          style={{
                            top: "5px",
                            right: "5px",
                            padding: "2px 6px",
                            zIndex: 10,
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={() => handleDeleteAttachment(index)}
                          title="Delete attachment"
                        >
                          ×
                        </button>

                        <img
                          src={previewUrls[index]}
                          alt={file.name}
                          className="card-img-top"
                          style={{
                            height: "150px",
                            objectFit: "cover",
                          }}
                        />

                        <div className="card-body p-2">
                          <p
                            className="card-text small mb-1 text-truncate"
                            title={file.name}
                          >
                            {file.name}
                          </p>
                          <small className="text-muted">
                            {(file.size / 1024).toFixed(2)} KB
                          </small>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            <div>
              {quoteData &&
              quoteData.attachments &&
              quoteData.attachments.length > 0 ? (
                <div className="my-3 row g-2">
                  {quoteData?.attachments.map((item) => (
                    <div
                      key={item.id}
                      className="col-12 col-sm-6 col-md-4  mb-1"
                    >
                      <div className="d-flex p-2 align-items-center justify-content-between br-7 dashed m-0">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="bg-greyF5"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 3,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Icon name="Link" />
                          </div>
                          <div className="fw-normal fs-14 text-grey89">
                            {(item.id.toString() || "").toUpperCase()} Asset.
                            {item.type}
                          </div>
                        </div>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            className="btn btn-sm dropdown"
                            tag="button"
                            type="button"
                          >
                            <i className="ri-more-fill align-middle"></i>
                          </DropdownToggle>

                          <DropdownMenu className="dropdown-menu-end">
                            <DropdownItem
                              type="button"
                              tag="span"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // openInNewTab(item.url);
                                window.open(
                                  item.url,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }}
                            >
                              <i className="ri-eye-fill me-2 text-muted"></i>{" "}
                              View
                            </DropdownItem>

                            <DropdownItem
                              type="button"
                              tag="span"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // downloadFile(item.url, `${item.id}.${item.type}`);
                                setDeleteModal(item.id);
                              }}
                            >
                              <i className="ri-delete-bin-fill me-2 text-muted"></i>
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="text-start mb-3">
            <CustomButton
              className="border-7 bg-primary w-15"
              text="Save"
              type="submit"
              loading={
                addQuoteLoading === "loading" || editQuoteLoading === "loading"
              }
            />
          </div>
        </form>
      </Col>

      <DeleteItemModal
        show={deleteModal !== null}
        onCloseClick={() => setDeleteModal(null)}
        onDeleteClick={async () => {
          if (deleteModal) {
            const attachmentId = deleteModal;
            if (quoteData) {
              dispatch(
                deleteAttachment({
                  quote_id: quoteData.id,
                  attachment_id: attachmentId,
                }),
              );
            }
          }
          setDeleteModal(null);
        }}
      />
      <ManageClientModal
        isEdit={false}
        isOpen={showClientModal}
        toggleModal={() => {
          setShowClientModal(false);
        }}
        initialData={null}
        onSubmit={(payload) => {
          dispatch(addNewClient(payload as AddClientFormParams));
        }}
      />
    </Row>
  );
};

export default memo(SummaryPane);
